import type {BadgeColorScheme} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {always} from 'ramda';

import type {RentalCalendarEventResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

export interface RentalStateAppearance {
  /** Subset of ColorSchemeType shared by Flag and Badge */
  colorScheme: BadgeColorScheme;
  label: string;
  /** CSS color of the event bar border/stripes (theme palette CSS variable) */
  cssColor: string;
}

/**
 * Canonical rental order state presentation for the calendar. Replaces the flag color/label the
 * `rental-order` data grid used to ship with each row.
 */
export function getRentalStateAppearance(
  state: RentalCalendarEventResponseBody['state'] | Nullish
): RentalStateAppearance {
  return match<RentalCalendarEventResponseBody['state'] | Nullish, RentalStateAppearance>(state)
    .with(
      'reserved',
      always({
        colorScheme: 'blue',
        label: i18n.t('page.rentalOrder.labels.reserved'),
        cssColor: 'var(--color-palettes-blue-70-100)',
      })
    )
    .with(
      'rented',
      always({
        colorScheme: 'orange',
        label: i18n.t('page.rentalOrder.labels.rented'),
        cssColor: 'var(--color-palettes-orange-60-100)',
      })
    )
    .with(
      'returned',
      always({
        colorScheme: 'purple',
        label: i18n.t('page.rentalOrder.labels.returned'),
        cssColor: 'var(--color-palettes-purple-60-100)',
      })
    )
    .with(
      'closed',
      always({
        colorScheme: 'green',
        label: i18n.t('page.rentalOrder.labels.closed'),
        cssColor: 'var(--color-palettes-green-60-100)',
      })
    )
    .with(
      'canceled',
      always({
        colorScheme: 'red',
        label: i18n.t('page.rentalOrder.labels.canceled'),
        cssColor: 'var(--color-palettes-red-60-100)',
      })
    )
    .with(
      Pattern.union('draft', Pattern.nullish),
      always({
        colorScheme: 'neutral',
        label: i18n.t('page.rentalOrder.labels.draft'),
        cssColor: 'var(--color-palettes-neutral-300-100)',
      })
    )
    .exhaustive();
}

export const BLOCK_COLOR = 'var(--color-palettes-yellow-80-100)';
